import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

import { usePermissions } from '../hooks/usePermissions';
import {
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Download,
  Star,
  TrendingUp,
  Smartphone
} from 'lucide-react';

const Apps = () => {
  const { canView, canCreate, canEdit, canDelete } = usePermissions();

  const [apps, setApps] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [newApp, setNewApp] = useState({
    name: '',
    description: '',
    packageName: '',
    platform: 'android',
    status: 'gelistirme',
    downloadLink: '',
    serverUrl: '',
    team: {
      developer: '',
      ideaOwner: '',
      analyst: '',
      marketer: ''
    }
  });

  const fetchApps = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/apps');
      if (response.data.success) {
        setApps(response.data.apps || []);
      }
    } catch (error) {
      console.error('Uygulama listesi hatası:', error);
      // API hatası durumunda fallback verileri yükle
      loadFallbackData();
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get('/team');
      if (response.data.success) {
        setTeamMembers(response.data.users || []);
      }
    } catch (error) {
      console.error('Team üyeleri hatası:', error);
      setTeamMembers([]);
    }
  };

  // Gerçek uygulama verilerini API'den çek
  useEffect(() => {
    fetchApps();
    fetchTeamMembers();
  }, [fetchApps]);

  const loadFallbackData = () => {
    // Demo veriler kaldırıldı - sadece gerçek API verilerini kullan
    console.log('API hatası - demo veriler yüklenmedi, boş liste gösteriliyor');
    setApps([]);
  };

  const handleDeleteApp = async () => {
    if (!selectedApp) return;

    try {
      const appId = selectedApp._id || selectedApp.id;
      const response = await axios.delete(`/apps/${appId}`);
      if (response.data.success) {
        setApps(apps.filter(app => (app._id || app.id) !== appId));
        setShowDeleteModal(false);
        setSelectedApp(null);
        alert('Uygulama başarıyla silindi!');
      }
    } catch (error) {
      console.error('Uygulama silme hatası:', error);
      // Fallback - local'den sil
      const appId = selectedApp._id || selectedApp.id;
      setApps(apps.filter(app => (app._id || app.id) !== appId));
      setShowDeleteModal(false);
      setSelectedApp(null);
      alert('Uygulama silindi (local)');
    }
  };

  const handleCreateApp = async (e) => {
    e.preventDefault();
    try {
      // Team alanlarını temizle - boş string'leri çıkar
      const appDataToSend = {
        ...newApp,
        team: Object.fromEntries(
          Object.entries(newApp.team).filter(([key, value]) => {
            if (!value) return false;
            if (typeof value === 'string') return value.trim() !== '';
            if (typeof value === 'object' && value._id) return true;
            return false;
          })
        )
      };

      const response = await axios.post('/apps', appDataToSend);
      if (response.data.success) {
        setApps([...apps, response.data.app]);
        setShowAddModal(false);
        setNewApp({
          name: '',
          description: '',
          packageName: '',
          platform: 'android',
          status: 'gelistirme',
          downloadLink: '',
          serverUrl: '',
          team: {
            developer: '',
            ideaOwner: '',
            analyst: '',
            marketer: ''
          }
        });
        alert('Uygulama başarıyla oluşturuldu!');
      }
    } catch (error) {
      console.error('Uygulama oluşturma hatası:', error);
      alert('Uygulama oluşturulurken hata oluştu!');
    }
  };

  const handleEditApp = async (e) => {
    e.preventDefault();
    try {
      const appId = selectedApp._id || selectedApp.id;
      const appDataToSend = {
        ...selectedApp,
        team: Object.fromEntries(
          Object.entries(selectedApp.team || {}).filter(([key, value]) => {
            if (!value) return false;
            if (typeof value === 'string') return value.trim() !== '';
            if (typeof value === 'object' && value._id) return true;
            return false;
          })
        )
      };

      const response = await axios.put(`/apps/${appId}`, appDataToSend);
      if (response.data.success) {
        setApps(apps.map(app =>
          (app._id || app.id) === appId ? response.data.app : app
        ));
        setShowEditModal(false);
        setSelectedApp(null);
        alert('Uygulama başarıyla güncellendi!');
      }
    } catch (error) {
      console.error('Uygulama güncelleme hatası:', error);
      alert('Uygulama güncellenirken hata oluştu!');
    }
  };

  // Loading durumunu kontrol et
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    const colors = {
      'gelistirme': 'bg-yellow-100 text-yellow-800',
      'test': 'bg-blue-100 text-blue-800',
      'canli': 'bg-green-100 text-green-800',
      'durduruldu': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusText = (status) => {
    const texts = {
      'gelistirme': 'Geliştirme',
      'test': 'Test',
      'canli': 'Canlı',
      'durduruldu': 'Durduruldu'
    };
    return texts[status] || status;
  };

  const getPlatformIcon = (platform) => {
    if (platform === 'both') return '📱';
    if (platform === 'android') return '🤖';
    if (platform === 'ios') return '🍎';
    return '📱';
  };

  const filteredApps = apps.filter(app => {
    const matchesSearch = app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.packageName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || app.status === filterStatus;
    return matchesSearch && matchesFilter;
  });



  // Yetki kontrolü
  if (!canView('apps')) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Smartphone className="h-8 w-8 text-red-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-red-800">
                Erişim Yetkisi Yok
              </h3>
              <p className="mt-2 text-sm text-red-700">
                Uygulamaları görüntülemek için gerekli yetkiniz bulunmuyor.
                Lütfen yöneticinizle iletişime geçin.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Başlık ve Eylemler */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Uygulamalar</h1>
          <p className="mt-1 sm:mt-2 text-sm text-gray-700">
            {canView('apps') ? 'Tüm mobil uygulamalarınızı buradan yönetebilirsiniz' : 'Uygulamaları görüntüleme yetkiniz bulunmuyor'}
          </p>
        </div>
        {canCreate('apps') && (
          <div className="mt-3 sm:mt-4 lg:mt-0">
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              Yeni Uygulama
            </button>
          </div>
        )}
      </div>

      {/* Arama ve Filtreler */}
      <div className="bg-white p-3 sm:p-4 rounded-lg shadow">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Uygulama ara..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="relative">
            <select
              className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">Tüm Durumlar</option>
              <option value="gelistirme">Geliştirme</option>
              <option value="test">Test</option>
              <option value="canli">Canlı</option>
              <option value="durduruldu">Durduruldu</option>
            </select>
          </div>

          <div className="flex items-center text-sm text-gray-500">
            <Filter className="h-4 w-4 mr-2" />
            {filteredApps.length} uygulama bulundu
          </div>
        </div>
      </div>

      {/* Uygulama Listesi */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {filteredApps.map((app) => (
            <li key={app._id || app.id} className="px-4 sm:px-6 py-4">
              <div className="flex items-start sm:items-center justify-between flex-col sm:flex-row space-y-3 sm:space-y-0">
                <div className="flex items-center w-full sm:w-auto">
                  <div className="flex-shrink-0 h-10 w-10 sm:h-12 sm:w-12">
                    <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                      <Smartphone className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                    </div>
                  </div>

                  <div className="ml-3 sm:ml-4 flex-1">
                    <div className="flex items-center flex-wrap">
                      <h3 className="text-base sm:text-lg font-medium text-gray-900">{app.name}</h3>
                      <span className="ml-2 text-lg">{getPlatformIcon(app.platform)}</span>
                      <span className={`ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(app.status)}`}>
                        {getStatusText(app.status)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500">{app.packageName}</p>
                    <div className="mt-2 flex items-center text-xs sm:text-sm text-gray-500 space-x-3 sm:space-x-4 flex-wrap">
                      <div className="flex items-center">
                        <Download className="h-4 w-4 mr-1" />
                        {app.metrics?.totalDownloads?.toLocaleString('tr-TR') || '0'}
                      </div>
                      <div className="flex items-center">
                        <Star className="h-4 w-4 mr-1 text-yellow-400" />
                        {app.metrics?.rating || '0.0'} ({app.metrics?.reviewCount || 0})
                      </div>
                      <div className="flex items-center">
                        <TrendingUp className="h-4 w-4 mr-1 text-green-500" />
                        ₺{app.revenue?.monthlyRevenue?.toLocaleString('tr-TR') || '0'}/ay
                      </div>
                      {app.downloadLink && (
                        <div className="flex items-center text-purple-600">
                          <i className="fas fa-link h-3 w-3 mr-1"></i>
                          Direkt Link
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-3 sm:space-x-4 w-full sm:w-auto justify-between sm:justify-end">
                  <div className="text-right">
                    <div className="text-base sm:text-lg font-semibold text-gray-900">
                      ₺{app.revenue?.totalRevenue?.toLocaleString('tr-TR') || '0'}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-500">Toplam Gelir</div>
                  </div>

                  <div className="flex items-center space-x-2">
                    {canEdit('apps') && (
                      <button
                        onClick={() => {
                          setSelectedApp({ ...app, team: app.team || {} });
                          setShowEditModal(true);
                        }}
                        className="p-2 rounded-full hover:bg-blue-100 text-blue-600"
                        title="Düzenle"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    )}
                    {canDelete('apps') && (
                      <button
                        onClick={() => {
                          setSelectedApp(app);
                          setShowDeleteModal(true);
                        }}
                        className="p-2 rounded-full hover:bg-red-100 text-red-600"
                        title="Sil"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                    {!canEdit('apps') && !canDelete('apps') && canView('apps') && (
                      <span className="text-xs text-gray-500 px-2 py-1 bg-gray-100 rounded">
                        Sadece Görüntüleme
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Ekip Bilgileri */}
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Geliştirici</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {app.team?.developer?.username || app.team?.developer || 'Atanmamış'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Fikir Sahibi</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {app.team?.ideaOwner?.username || app.team?.ideaOwner || 'Atanmamış'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Analizci</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {app.team?.analyst?.username || app.team?.analyst || 'Atanmamış'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Pazarlamacı</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {app.team?.marketer?.username || app.team?.marketer || 'Atanmamış'}
                  </dd>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {filteredApps.length === 0 && (
        <div className="text-center py-12">
          <Smartphone className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {apps.length === 0 ? 'Henüz uygulama eklenmemiş' : 'Uygulama bulunamadı'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {apps.length === 0
              ? 'Sisteme ilk uygulamanızı eklemek için "Yeni Uygulama" butonunu kullanın.'
              : 'Arama kriterlerinize uygun uygulama bulunamadı.'
            }
          </p>
          {apps.length === 0 && canCreate('apps') && (
            <button
              onClick={() => setShowAddModal(true)}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              İlk Uygulamanızı Ekleyin
            </button>
          )}
        </div>
      )}

      {/* Yeni Uygulama Ekleme Modalı */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Yeni Uygulama Ekle</h3>

              <form onSubmit={handleCreateApp} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Uygulama Adı</label>
                    <input
                      type="text"
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      value={newApp.name}
                      onChange={(e) => setNewApp({ ...newApp, name: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Paket Adı</label>
                    <input
                      type="text"
                      required
                      placeholder="com.gencgirisimci.uygulama"
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      value={newApp.packageName}
                      onChange={(e) => setNewApp({ ...newApp, packageName: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                  <textarea
                    rows={3}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={newApp.description}
                    onChange={(e) => setNewApp({ ...newApp, description: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">İndirme Linki</label>
                  <input
                    type="url"
                    placeholder="https://example.com/app-download-link"
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={newApp.downloadLink}
                    onChange={(e) => setNewApp({ ...newApp, downloadLink: e.target.value })}
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Şirket sitesindeki indirme butonunda kullanılacak direkt indirme linki
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Server URL</label>
                  <input
                    type="url"
                    placeholder="https://app-backend.onrender.com"
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={newApp.serverUrl}
                    onChange={(e) => setNewApp({ ...newApp, serverUrl: e.target.value })}
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Uygulamanın backend server URL'i (istatistikler için)
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Platform</label>
                    <select
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      value={newApp.platform}
                      onChange={(e) => setNewApp({ ...newApp, platform: e.target.value })}
                    >
                      <option value="android">Android</option>
                      <option value="ios">iOS</option>
                      <option value="both">Her İkisi</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Durum</label>
                    <select
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      value={newApp.status}
                      onChange={(e) => setNewApp({ ...newApp, status: e.target.value })}
                    >
                      <option value="gelistirme">Geliştirme</option>
                      <option value="test">Test</option>
                      <option value="canli">Canlı</option>
                      <option value="durduruldu">Durduruldu</option>
                    </select>
                  </div>
                </div>

                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Ekip Atamaları (Opsiyonel)</h4>
                  <p className="text-sm text-gray-500 mb-4">
                    Ekip üyelerini daha sonra da atayabilirsiniz. Şimdilik boş bırakabilirsiniz.
                  </p>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Geliştirici</label>
                      <select
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        value={newApp.team.developer}
                        onChange={(e) => setNewApp({ ...newApp, team: { ...newApp.team, developer: e.target.value } })}
                      >
                        <option value="">Seçiniz (opsiyonel)</option>
                        {teamMembers.filter(member => member.role === 'gelistirici' || member.role === 'yonetici').map(member => (
                          <option key={member._id || member.id} value={member.username}>
                            {member.username} - {member.email}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Fikir Sahibi</label>
                      <select
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        value={newApp.team.ideaOwner}
                        onChange={(e) => setNewApp({ ...newApp, team: { ...newApp.team, ideaOwner: e.target.value } })}
                      >
                        <option value="">Seçiniz (opsiyonel)</option>
                        {teamMembers.map(member => (
                          <option key={member._id || member.id} value={member.username}>
                            {member.username} - {member.email}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Analizci</label>
                      <select
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        value={newApp.team.analyst}
                        onChange={(e) => setNewApp({ ...newApp, team: { ...newApp.team, analyst: e.target.value } })}
                      >
                        <option value="">Seçiniz (opsiyonel)</option>
                        {teamMembers.filter(member => member.role === 'analizci' || member.role === 'yonetici').map(member => (
                          <option key={member._id || member.id} value={member.username}>
                            {member.username} - {member.email}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Pazarlamacı</label>
                      <select
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        value={newApp.team.marketer}
                        onChange={(e) => setNewApp({ ...newApp, team: { ...newApp.team, marketer: e.target.value } })}
                      >
                        <option value="">Seçiniz (opsiyonel)</option>
                        {teamMembers.filter(member => member.role === 'pazarlamaci' || member.role === 'yonetici').map(member => (
                          <option key={member._id || member.id} value={member.username}>
                            {member.username} - {member.email}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                  >
                    Oluştur
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Uygulama Düzenleme Modalı */}
      {showEditModal && selectedApp && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Uygulama Düzenle</h3>

              <form onSubmit={handleEditApp} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Uygulama Adı</label>
                    <input
                      type="text"
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      value={selectedApp.name}
                      onChange={(e) => setSelectedApp({ ...selectedApp, name: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Paket Adı</label>
                    <input
                      type="text"
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      value={selectedApp.packageName}
                      onChange={(e) => setSelectedApp({ ...selectedApp, packageName: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                  <textarea
                    rows={3}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={selectedApp.description || ''}
                    onChange={(e) => setSelectedApp({ ...selectedApp, description: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">İndirme Linki</label>
                  <input
                    type="url"
                    placeholder="https://example.com/app-download-link"
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={selectedApp.downloadLink || ''}
                    onChange={(e) => setSelectedApp({ ...selectedApp, downloadLink: e.target.value })}
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Şirket sitesindeki indirme butonunda kullanılacak direkt indirme linki
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Server URL</label>
                  <input
                    type="url"
                    placeholder="https://app-backend.onrender.com"
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={selectedApp.serverUrl || ''}
                    onChange={(e) => setSelectedApp({ ...selectedApp, serverUrl: e.target.value })}
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Uygulamanın backend server URL'i (istatistikler için)
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Platform</label>
                    <select
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      value={selectedApp.platform}
                      onChange={(e) => setSelectedApp({ ...selectedApp, platform: e.target.value })}
                    >
                      <option value="android">Android</option>
                      <option value="ios">iOS</option>
                      <option value="both">Her İkisi</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Durum</label>
                    <select
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      value={selectedApp.status}
                      onChange={(e) => setSelectedApp({ ...selectedApp, status: e.target.value })}
                    >
                      <option value="gelistirme">Geliştirme</option>
                      <option value="test">Test</option>
                      <option value="canli">Canlı</option>
                      <option value="durduruldu">Durduruldu</option>
                    </select>
                  </div>
                </div>

                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Ekip Atamaları</h4>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Geliştirici</label>
                      <select
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        value={selectedApp.team?.developer?.username || selectedApp.team?.developer || ''}
                        onChange={(e) => setSelectedApp({ ...selectedApp, team: { ...selectedApp.team, developer: e.target.value } })}
                      >
                        <option value="">Seçiniz (opsiyonel)</option>
                        {teamMembers.filter(member => member.role === 'gelistirici' || member.role === 'yonetici').map(member => (
                          <option key={member._id || member.id} value={member.username}>
                            {member.username} - {member.email}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Fikir Sahibi</label>
                      <select
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        value={selectedApp.team?.ideaOwner?.username || selectedApp.team?.ideaOwner || ''}
                        onChange={(e) => setSelectedApp({ ...selectedApp, team: { ...selectedApp.team, ideaOwner: e.target.value } })}
                      >
                        <option value="">Seçiniz (opsiyonel)</option>
                        {teamMembers.map(member => (
                          <option key={member._id || member.id} value={member.username}>
                            {member.username} - {member.email}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Analizci</label>
                      <select
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        value={selectedApp.team?.analyst?.username || selectedApp.team?.analyst || ''}
                        onChange={(e) => setSelectedApp({ ...selectedApp, team: { ...selectedApp.team, analyst: e.target.value } })}
                      >
                        <option value="">Seçiniz (opsiyonel)</option>
                        {teamMembers.filter(member => member.role === 'analizci' || member.role === 'yonetici').map(member => (
                          <option key={member._id || member.id} value={member.username}>
                            {member.username} - {member.email}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Pazarlamacı</label>
                      <select
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        value={selectedApp.team?.marketer?.username || selectedApp.team?.marketer || ''}
                        onChange={(e) => setSelectedApp({ ...selectedApp, team: { ...selectedApp.team, marketer: e.target.value } })}
                      >
                        <option value="">Seçiniz (opsiyonel)</option>
                        {teamMembers.filter(member => member.role === 'pazarlamaci' || member.role === 'yonetici').map(member => (
                          <option key={member._id || member.id} value={member.username}>
                            {member.username} - {member.email}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setSelectedApp(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                  >
                    Güncelle
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Silme Onay Modalı */}
      {showDeleteModal && selectedApp && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <div className="mt-3 text-center">
                <h3 className="text-lg font-medium text-gray-900">Uygulamayı Sil</h3>
                <div className="mt-2 px-7 py-3">
                  <p className="text-sm text-gray-500">
                    <strong>{selectedApp.name}</strong> uygulamasını silmek istediğinizden emin misiniz?
                    Bu işlem geri alınamaz ve tüm veriler silinecektir.
                  </p>
                </div>
                <div className="flex justify-center space-x-3 mt-4">
                  <button
                    onClick={() => {
                      setShowDeleteModal(false);
                      setSelectedApp(null);
                    }}
                    className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md hover:bg-gray-400"
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleDeleteApp}
                    className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md hover:bg-red-700"
                  >
                    Sil
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Apps;